/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VlcEEComponentAddDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import Stack from "@mui/material/Stack";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";


import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../redux/actions";
import actionType from "../../redux/actionTypes";
import moment from "moment";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";
import MultiselectConnectorV2 from "../../components/customComponentsV2/common/connectors/MultiselectConnectorV2";
import { Box } from "@mui/material";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomTabsV2 from "../../components/customComponentsV2/common/tabs/CustomTabsV2";
import AttributesForm from "./Attributes/AttributesForm";
import SelectVehicleComponentForVehicleSystem from "./SelectVehicleComponentForVehicleSystem";

function VlcEEComponentAddDialog({
  title,
  getEEComponentsList,
  getZoneEECountList,
  selectedZone,
  selectedZoneDetails,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  selectedVariantsMany,
  setSelectedVariantsMany,
  openEEComponentAddDialog,
  variantOptionList,
  setOpenEEComponentAddDialog,
  handleEEComponentAdd,
  vsOptionsList,
  selectedEEComponentDetails,
  adminSettingsDetails,
  supplierOptionsList,
}) {
  const dispatch = useDispatch();
  /* Add EE Components Fields */
  const [assemblyNo, setAssemblyNo] = useState("");
  const [assemblyNoError, setAssemblyNoError] = useState("");
  const [supplierName, setSupplierName] = useState(null);
  const [supplierNameError, setSupplierNameError] = useState("");
  const [version, setVersion] = useState("");
  const [versionError, setVersionError] = useState("");
  const [vcId, setVcId] = useState("");
  const [vcIdError, setVcIdError] = useState(false);
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [selectedVSIds, setSelectedVSIds] = useState([]);
  const [selectedASIL, setSelectedASIL] = useState(null);
  const [attributeFormData, setAttributeFormData] = useState({
    added: [],
    edited: [],
    deleted: [],
  });
  const [addTabValue, setAddTabValue] = useState(0);

  const handleAttributeFormData = useCallback((data = {}) => {
    const nextData = {
      added: Array.isArray(data?.added) ? data.added : [],
      edited: Array.isArray(data?.edited) ? data.edited : [],
      deleted: Array.isArray(data?.deleted) ? data.deleted : [],
    };
    setAttributeFormData((prev) =>
      JSON.stringify(prev) === JSON.stringify(nextData) ? prev : nextData,
    );
  }, []);

  const addedAttributeFormRows = useMemo(
    () =>
      (Array.isArray(attributeFormData?.added)
        ? attributeFormData.added
        : []
      ).map((attribute) => ({
        ...attribute,
        attribute_id: null,
        newlyAdded: true,
        edited: false,
      })),
    [attributeFormData?.added],
  );

  const handleSelectedVehicleSystem = useCallback((vehicleSystemId) => {
    setSelectedVSIds(vehicleSystemId ? [vehicleSystemId] : []);
    setVcId("");
    setVcIdError(false);
  }, []);

  const handleSelectedVehicleComponent = useCallback((vehicleComponentId) => {
    setVcId(vehicleComponentId || "");
    setVcIdError(false);
  }, []);

  /* -------------- UseEffects -------------- */

  useEffect(() => {
    if (
      openEEComponentAddDialog &&
      selectedSingleVariantDetails?.variant_id != null
    ) {
      const id = selectedSingleVariantDetails.variant_id;
      setSelectedVariantsMany((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const hasId = prevArr.map(String).includes(String(id));
        if (hasId) return prevArr;
        return [id, ...prevArr];
      });
    }
    setAddTabValue(0);
  }, [
    openEEComponentAddDialog,
    selectedSingleVariantDetails,
    setSelectedVariantsMany,
  ]);

  /* -------------- Add EE Component -------------- */

  const clearAddEEComponentFields = () => {
    setAssemblyNo("");
    setAssemblyNoError("");
    setSupplierName(null);
    setSupplierNameError("");
    setVersion("");
    setVersionError("");
    setDescription("");
    setDescriptionError("");
    setSummary("");
    setSummaryError("");
    setVcId("");
    setVcIdError(false);
    setSelectedVSIds([]);
    setSelectedASIL(null);
    setSelectedVariantsMany([]);
    setAttributeFormData({ added: [], edited: [], deleted: [] });
  };

  const isAddEEComponentValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
    return (
      // selectedVariantsMany?.length > 0 &&
      assemblyNo?.trim() !== "" &&
      assemblyNo?.trim().length >= 3 &&
      !assemblyNoError &&
      regexMin3.test(assemblyNo.trim()) &&
      supplierName?.length > 0 &&
      version?.trim() !== "" &&
      version?.trim().length >= 3 &&
      !versionError &&
      regexMin3.test(version.trim()) &&
      description?.trim() !== "" &&
      description?.trim().length >= 3 &&
      !descriptionError &&
      // selectedVSIds?.length > 0 &&
      // vcId !== "" &&
      // vcId != null &&
      // !vcIdError &&
      selectedASIL?.length > 0 &&
      (summary?.trim().length === 0 || !summaryError) &&
      (summary?.trim().length === 0 || summary.trim().length >= 3)
    );
  };

  const isAddEEComponentDraftValid = () => {
    return (
      assemblyNo?.trim() !== "" ||
      supplierName?.length > 0 ||
      version?.trim() !== "" ||
      description?.trim() !== "" ||
      summary?.trim() !== "" ||
      selectedVSIds?.length > 0 ||
      Boolean(vcId) ||
      selectedASIL?.length > 0
    );
  };

  const handleCloseEEComponentAdd = () => {
    setOpenEEComponentAddDialog(false);

    clearAddEEComponentFields();
  };

  const handleAddEEComponent = () => {
    handleEEComponentAdd({
      variant_ids: selectedVariantsMany,
      save_as_draft: false,
      assembly_number: assemblyNo?.trim(),
      supplier_name: supplierName,
      version: version?.trim(),
      summary: summary,
      ...(vcId ? { vc_id: Number(vcId) } : null),
      description: description,
      ...(selectedVSIds?.length > 0
        ? { vehicle_systems: selectedVSIds?.map(Number) || [] }
        : null),
      other_fields: {
        asil_class: selectedASIL,
      },
      attributes: attributeFormData?.added || [],
    });
    handleCloseEEComponentAdd();
  };

  const handleAddEEComponentDraft = () => {
    const reqObj = {
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedSingleVariantDetails?.variant_id,

      ...(selectedProjectDetails?.is_zonal && {
        zone_position: selectedProjectDetails?.project_zones?.find(
          (zones) => zones?.zone_name === selectedZone?.zone_position_name,
        )?.position,
      }),
      variant_ids: selectedVariantsMany,
      save_as_draft: true,
      assembly_number: assemblyNo?.trim(),
      ...(supplierName ? { supplier_name: supplierName } : null),

      ...(version ? { version: version?.trim() } : null),
      ...(summary ? { summary: summary } : null),
      ...(description ? { description: description } : null),
      ...(selectedVSIds?.length > 0
        ? { vehicle_systems: selectedVSIds?.map(Number) || [] }
        : null),
      ...(vcId ? { vc_id: Number(vcId) } : null),
      ...(selectedASIL?.length > 0
        ? {
          other_fields: {
            asil_class: selectedASIL,
          },
        }
        : null),
      attributes: attributeFormData?.added || [],
      current_time: moment().format(),
    };

    dispatch(
      HandleApiActions({
        api_name: "/VLC/ee_components/add",
        https_method: "POST",
        action_type: actionType.Add_EE_COMPONENT,
        description: "Add in Save As Draft EE Component",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      getEEComponentsList();
      handleCloseEEComponentAdd();
    });
  };

  return (
    <Box>

      <CustomDialogBoxV2
        open={openEEComponentAddDialog}
        onClose={() => {
          setOpenEEComponentAddDialog(false);
          clearAddEEComponentFields();
        }}
        title="Add EE Component"
        onClickSuccess={() => handleAddEEComponentDraft()}
        onClickReject={() => setOpenEEComponentAddDialog(false)}
        dialogActions={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box>
              {addTabValue === 1 ? (
                <CustomButtonV2
                  type="cancel"
                  size="small"
                  btnName={"Back"}
                  handleOnClick={() => setAddTabValue(0)}
                />
              ) : null}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {addTabValue === 1 ? (
                <CustomButtonV2
                  type="other"
                  size="small"
                  disabled={!isAddEEComponentDraftValid()}
                  btnName={"Save Draft"}
                  handleOnClick={() => {
                    handleAddEEComponentDraft();
                  }}
                />
              ) : null}

              <CustomButtonV2
                type="submit"
                size="small"
                disabled={
                  addTabValue === 0
                    ? !isAddEEComponentDraftValid()
                    : !isAddEEComponentValid()
                }
                btnName={addTabValue === 0 ? "Next" : "Add"}
                handleOnClick={() => {
                  if (addTabValue === 0) {
                    setAddTabValue(1);
                  } else {
                    handleAddEEComponent();
                  }
                }}
              />
            </Box>
          </Box>
        }
      >
        <CustomTabsV2
          defaultTab={addTabValue}
          value={addTabValue}
          onTabChange={(newValue) => setAddTabValue(newValue)}
          tabs={[
            {
              name: "Details",
              content: (
                <Stack spacing={2} mb={2} sx={{ width: "450px" }}>
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Assembly No"}
                    required={true}
                    type={"text"}
                    fldType={"textR"}
                    value={assemblyNo}
                    setValue={(value) =>
                      value.length <= 40 && setAssemblyNo(value)
                    }
                    maxLength={40}
                  />
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Version"}
                    required={true}
                    type={"text"}
                    fldType="textR"
                    value={version}
                    setValue={(value) =>
                      value.length <= 40 && setVersion(value)
                    }
                    maxLength={40}
                  />
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Description"}
                    required={true}
                    value={description}
                    setValue={(value) =>
                      value.length <= 500 && setDescription(value)
                    }
                    maxLength={500}
                  />
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Summary"}
                    type={"text"}
                    fldType={"textO"}
                    value={summary}
                    setValue={(value) =>
                      value.length <= 250 && setSummary(value)
                    }
                    valueError={summaryError}
                    setValueError={setSummaryError}
                    maxLength={250}
                  />
                  <CustomSelectV2
                    size={"small"}
                    label={"Select Supplier Name"}
                    optionsList={supplierOptionsList}
                    selectedValue={supplierName || " "}
                    handleOnChange={(e) => setSupplierName(e.target.value)}
                  />

                  <SelectVehicleComponentForVehicleSystem
                    open={openEEComponentAddDialog}
                    projectId={selectedProjectDetails?.project_id}
                    variantId={selectedSingleVariantDetails?.variant_id}
                    isZonal={selectedProjectDetails?.is_zonal}
                    zonePosition={selectedZoneDetails?.position}
                    getSelectedVehicleSystem={handleSelectedVehicleSystem}
                    getSelectedVehicleComponent={handleSelectedVehicleComponent}
                    setVcIdError={setVcIdError}
                    vsId={selectedVSIds[0]}   // 👈 pass the selected system id
                    vcId={vcId}               // 👈 pass the selected component id
                  />
                  <CustomSelectV2
                    size={"small"}
                    label={"Select ASIL Class"}
                    required={true}
                    optionsList={["A", "B", "C", "D"]?.map((el) => ({
                      value: el,
                      label: el,
                    }))}
                    selectedValue={selectedASIL || " "}
                    handleOnChange={(e) => setSelectedASIL(e.target.value)}
                  />
                  <MultiselectConnectorV2
                    multiple
                    selectedValues={selectedVariantsMany || " "}
                    setSelectedValues={setSelectedVariantsMany}
                    label={"Select Variants"}
                    optionsList={variantOptionList}
                    placeholder={false}
                    immutableValues={
                      selectedSingleVariantDetails?.variant_id != null
                        ? [selectedSingleVariantDetails?.variant_id]
                        : []
                    }
                  />
                </Stack>
              ),
            },
            {
              name: "Attribute",
              content: (
                <Stack
                  spacing={2}
                  mb={2}
                  justifyContent={"center"}
                  sx={{ width: "450px" }}
                >
                  <AttributesForm
                    type={"ADD"}
                    attributeFromApi={addedAttributeFormRows}
                    getFormData={handleAttributeFormData}
                  />
                </Stack>
              ),
            },
          ]}
        />
      </CustomDialogBoxV2>
    </Box>
  );
}

export default VlcEEComponentAddDialog;
